/**
 * Guest Conversions — who became a LifeScribe member, and on what plan.
 *
 * A separate page from Revenue rather than another panel on it, for two
 * reasons that would otherwise fight each other on one screen. Revenue is
 * windowed and aggregate; this is an all-time people-list you search, sort,
 * page and click. And Revenue's own caption already apologises for the range
 * mismatch — bolting a paginated table with its own filters onto it would
 * compound that rather than resolve it. Revenue is "money in from couples";
 * this is "members out to LifeScribe".
 *
 * ⚠️ THE PLAN COLUMN IS A SNAPSHOT, NOT ATTRIBUTION.
 * At the moment of conversion every account is created on `trial` — the
 * provisioning endpoint hardcodes it — and any paid plan arrives later,
 * outside the wedding tables, with nothing writing back. So the plan shown is
 * where the member is TODAY. The server says so explicitly with
 * `plan_is_point_in_time`, and this page prints the caveat rather than letting
 * a reader assume the wedding funnel sold that subscription.
 */

import React from 'react';
import {
  Box, Typography, Stack, TextField, MenuItem, Button, Chip, Tooltip,
  Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Alert, Link,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsightsIcon from '@mui/icons-material/Insights';
import {
  DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel,
} from '@mui/x-data-grid';
import EmptyState from '../../common/EmptyState';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  ConvertedGuest, ConvertedGuestsResponse,
} from '../weddingApi';
import { num, shortDate } from '../weddingFormat';

const WeddingConversionsPage: React.FC = () => {
  const [data, setData] = React.useState<ConvertedGuestsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [channel, setChannel] = React.useState<'all' | 'web' | 'app'>('all');
  const [matched, setMatched] = React.useState<'all' | 'matched' | 'new'>('all');
  const [pagination, setPagination] = React.useState<GridPaginationModel>({ page: 0, pageSize: 50 });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await weddingApi.getConvertedGuests({
        page: pagination.page + 1,
        limit: pagination.pageSize,
        channel: channel === 'all' ? undefined : channel,
        matched: matched === 'all' ? undefined : matched,
      }));
    } catch (e) {
      setError((e as WeddingApiError).message || 'Could not load conversions');
    } finally {
      setLoading(false);
    }
  }, [pagination, channel, matched]);

  React.useEffect(() => { load(); }, [load]);

  const columns: GridColDef<ConvertedGuest>[] = [
    {
      field: 'first_name',
      headerName: 'Guest',
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {p.row.first_name
              ? `${p.row.first_name} ${p.row.last_name || ''}`.trim()
              : 'Guest since deleted'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.guest_email || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'wedding_code',
      headerName: 'Book',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        <Tooltip title={`${p.row.partner1_name} & ${p.row.partner2_name}`}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {p.row.wedding_code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'plan_name',
      headerName: 'Plan today',
      width: 190,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.plan_name || 'Not linked'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.subscription_status || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'ever_paid',
      headerName: 'Ever paid',
      width: 110,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        <Chip
          size="small"
          variant="outlined"
          label={p.row.ever_paid ? 'Yes' : 'No'}
          color={p.row.ever_paid ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'channel',
      headerName: 'Channel',
      width: 110,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        <Typography variant="body2" color="text.secondary">
          {p.row.channel || 'unknown'}
        </Typography>
      ),
    },
    {
      field: 'matched_existing',
      headerName: 'New member',
      width: 140,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        p.row.matched_existing ? (
          <Tooltip title="This person already had a LifeScribe account — the wedding funnel did not create it.">
            <Chip size="small" variant="outlined" color="warning" label="Already a member" />
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={0.5}>
            <Chip size="small" variant="outlined" color="success" label="New" />
            {p.row.matched_by_email && (
              <Tooltip title="Linked by email address — no user id was recorded at conversion.">
                <Chip size="small" variant="outlined" label="by email" />
              </Tooltip>
            )}
          </Stack>
        )
      ),
    },
    {
      field: 'converted_at',
      headerName: 'Converted',
      width: 130,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<ConvertedGuest>) => (
        <Typography variant="body2" color="text.secondary">
          {shortDate(p.row.converted_at)}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <InsightsIcon color="primary" />
        <Typography variant="h5">Guest Conversions</Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Every account created through the wedding cross-sell starts on the free
        trial, and any paid plan is chosen later. <strong>The plan column is
        where each member is today</strong> — not what the wedding funnel sold
        them. &ldquo;Ever paid&rdquo; is the honest signal of whether a
        conversion turned into revenue.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Channel" value={channel}
          onChange={(e) => setChannel(e.target.value as typeof channel)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All channels</MenuItem>
          <MenuItem value="web">Web</MenuItem>
          <MenuItem value="app">App</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Membership" value={matched}
          onChange={(e) => setMatched(e.target.value as typeof matched)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">Everyone</MenuItem>
          <MenuItem value="new">New members only</MenuItem>
          <MenuItem value="matched">Already had an account</MenuItem>
        </TextField>
      </Stack>

      {data && data.by_plan.length > 0 && (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Card sx={{ flex: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Which plans converted guests are on
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                People who already had an account are excluded — counting their
                plan would credit the wedding funnel with a subscription it did
                not produce.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Plan</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell align="right">Guests</TableCell>
                    <TableCell align="right">Active</TableCell>
                    <TableCell align="right">Ever paid</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_plan.map((r) => (
                    <TableRow key={r.plan_code}>
                      <TableCell>{r.plan_name}</TableCell>
                      <TableCell>{r.display_tier || '—'}</TableCell>
                      <TableCell align="right">{num(r.guests)}</TableCell>
                      <TableCell align="right">{num(r.active)}</TableCell>
                      <TableCell align="right">{num(r.ever_paid)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Where they wrote</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                The contribution channel recorded since the dual-path release.
                &ldquo;unknown&rdquo; is every conversion that predates it — never
                backfilled, because the product did not record it.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell align="right">Guests</TableCell>
                    <TableCell align="right">New</TableCell>
                    <TableCell align="right">Ever paid</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_channel.map((r) => (
                    <TableRow key={r.channel}>
                      <TableCell>{r.channel}</TableCell>
                      <TableCell align="right">{num(r.guests)}</TableCell>
                      <TableCell align="right">{num(r.new_members)}</TableCell>
                      <TableCell align="right">{num(r.ever_paid)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      )}

      {error && !loading && !data ? (
        <EmptyState
          icon="error"
          title="Could not load conversions"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      ) : (
        <DataGrid
          rows={data ? data.guests : []}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.conversion_id}
          rowCount={data ? data.pagination.total : 0}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      )}
    </Box>
  );
};

export default WeddingConversionsPage;
